// Day armory gacha core.
//
// This module intentionally owns only catalogue identity, economy, deterministic
// draws and save-shape migration. Combat definitions stay in gb/gear.js and are
// supplied to hydrateGear as PART_BY_ID (or the gear module itself). The shared
// slot-specific trait vocabulary is imported so catalogue and combat cannot drift.

import {
  TRAIT_IDS as GEAR_TRAIT_IDS,
  deterministicTraits,
} from '../gb/gear.js';

export const ARMORY_VERSION = 3;
export const PITY_5_PLUS = 50;
export const PULL_COSTS = Object.freeze({ 1: 30, 10: 270 });
export const UPGRADE_COPY_COST = 3;
export const TIER_PROBABILITIES = Object.freeze([44, 28, 16, 8, 3.5, 0.5]);
export const TRAIT_IDS = GEAR_TRAIT_IDS;

const SLOT_DEFINITIONS_MUTABLE = [
  {
    id: 'stone',
    ko: '돌',
    suffix: null,
    kinds: [
      { id: 'jandol', setId: 'dry', ko: '가벼운 돌' },
      { id: 'goreun', setId: 'loose', ko: '손에 익은 돌' },
      { id: 'napjak', setId: 'rough', ko: '매끈한 돌' },
      { id: 'monan', setId: 'tamed', ko: '거친 돌' },
      { id: 'chadol', setId: 'aged', ko: '단단한 돌' },
      { id: 'mugeo', setId: 'hard', ko: '무거운 돌' },
    ],
  },
  {
    id: 'cradle',
    ko: '받침',
    suffix: '받침',
    kinds: [
      { id: 'dry', setId: 'dry' },
      { id: 'loose', setId: 'loose' },
      { id: 'rough', setId: 'rough' },
      { id: 'tamed', setId: 'tamed' },
      { id: 'aged', setId: 'aged' },
      { id: 'hard', setId: 'hard' },
    ],
  },
  {
    id: 'cord',
    ko: '끈',
    suffix: '끈',
    kinds: [
      { id: 'dry', setId: 'dry' },
      { id: 'loose', setId: 'loose' },
      { id: 'rough', setId: 'rough' },
      { id: 'tamed', setId: 'tamed' },
      { id: 'aged', setId: 'aged' },
      { id: 'hard', setId: 'hard' },
    ],
  },
  {
    id: 'loop',
    ko: '걸이',
    suffix: '고리',
    kinds: [
      { id: 'dry', setId: 'dry' },
      { id: 'loose', setId: 'loose' },
      { id: 'rough', setId: 'rough' },
      { id: 'tamed', setId: 'tamed' },
      { id: 'aged', setId: 'aged' },
      { id: 'hard', setId: 'hard' },
    ],
  },
];

const SET_KO = Object.freeze({
  dry: '가벼운',
  loose: '손에 익은',
  rough: '매끈한',
  tamed: '거친',
  aged: '단단한',
  hard: '무거운',
});

for (const slot of SLOT_DEFINITIONS_MUTABLE) {
  for (const kind of slot.kinds) Object.freeze(kind);
  Object.freeze(slot.kinds);
  Object.freeze(slot);
}
export const SLOT_DEFINITIONS = Object.freeze(SLOT_DEFINITIONS_MUTABLE);
export const SLOTS = Object.freeze(SLOT_DEFINITIONS.map((slot) => slot.id));
export const SLOT_KO = Object.freeze(Object.fromEntries(
  SLOT_DEFINITIONS.map((slot) => [slot.id, slot.ko]),
));

export function partKey(slot, id, tier) {
  return `${slot}:${id}:${tier | 0}`;
}

function hash32(text) {
  let h = 0x811c9dc5;
  const value = String(text);
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  return h >>> 0;
}

function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

let fallbackSeedCounter = 0;

function defaultGachaSeedSource() {
  if (globalThis.crypto?.getRandomValues) {
    const words = new Uint32Array(4);
    globalThis.crypto.getRandomValues(words);
    return Array.from(words, (word) => word.toString(16).padStart(8, '0')).join('');
  }
  fallbackSeedCounter = (fallbackSeedCounter + 1) >>> 0;
  return [
    Date.now().toString(36),
    fallbackSeedCounter.toString(36),
    Math.random().toString(36).slice(2),
  ].join('-');
}

function canonicalGachaSeed(value) {
  if (typeof value === 'string') return value.length ? value : null;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'bigint') return String(value);
  return null;
}

/** Create the separately persisted seed used only by armory pulls. */
export function createGachaSeed(seedSource = defaultGachaSeedSource) {
  if (typeof seedSource !== 'function') {
    throw new TypeError('createGachaSeed: seedSource must be a function');
  }
  const seed = canonicalGachaSeed(seedSource());
  if (!seed) throw new TypeError('createGachaSeed: seedSource returned an invalid seed');
  return seed;
}

function gearIcon(slot, id) {
  return slot === 'stone'
    ? `art/gear/v2/gear-stone-${id}.png`
    : `art/gear/v2/gear-${slot}-${id}.png`;
}

const catalogue = [];
for (const slot of SLOT_DEFINITIONS) {
  for (const kind of slot.kinds) {
    for (let tier = 1; tier <= 6; tier += 1) {
      const key = partKey(slot.id, kind.id, tier);
      catalogue.push(Object.freeze({
        key,
        slot: slot.id,
        slotKo: slot.ko,
        id: kind.id,
        setId: kind.setId,
        ko: kind.ko ?? `${SET_KO[kind.setId]} ${slot.suffix}`,
        tier,
        traits: Object.freeze(deterministicTraits(slot.id, kind.id, tier)),
        icon: gearIcon(slot.id, kind.id),
      }));
    }
  }
}

export const CATALOG = Object.freeze(catalogue);
export const GACHA_CATALOG = CATALOG;
export const catalog = CATALOG;
export const CATALOG_BY_KEY = Object.freeze(Object.fromEntries(
  CATALOG.map((entry) => [entry.key, entry]),
));

if (CATALOG.length !== 144 || new Set(CATALOG.map((entry) => entry.key)).size !== 144) {
  throw new Error('Armory catalogue must contain exactly 144 unique entries.');
}
if (Math.abs(TIER_PROBABILITIES.reduce((sum, value) => sum + value, 0) - 100) > 1e-9) {
  throw new Error('Armory tier probabilities must total 100%.');
}

export const START_LOADOUT = Object.freeze({
  stone: 'stone:goreun:1',
  cradle: 'cradle:loose:1',
  cord: 'cord:rough:1',
  loop: 'loop:tamed:1',
});

// The first story choice is a second, unequipped stone.  Keep this contract
// separate from START_LOADOUT: combat still starts with the familiar stone,
// while the reserve can be equipped and switched back without spending or RNG.
export const STARTER_RESERVE = Object.freeze({
  slot: 'stone',
  key: 'stone:jandol:1',
  count: 1,
  source: 'story',
});

export function parsePartKey(key) {
  const entry = typeof key === 'string' ? CATALOG_BY_KEY[key] : null;
  return entry ? { slot: entry.slot, id: entry.id, tier: entry.tier } : null;
}

export function traitsForKey(key) {
  const entry = CATALOG_BY_KEY[key];
  return entry ? entry.traits.slice() : null;
}

function emptyInventory() {
  return {};
}

function emptyDiscovered() {
  return {};
}

function freshArmory(options = {}, { includeStarterReserve = true } = {}) {
  const inventory = emptyInventory();
  const discovered = emptyDiscovered();
  for (const key of Object.values(START_LOADOUT)) {
    inventory[key] = 1;
    discovered[key] = true;
  }
  if (includeStarterReserve) {
    inventory[STARTER_RESERVE.key] = STARTER_RESERVE.count;
    discovered[STARTER_RESERVE.key] = true;
  }
  return {
    version: ARMORY_VERSION,
    gachaSeed: canonicalGachaSeed(options.gachaSeed)
      ?? createGachaSeed(options.seedSource),
    inventory,
    discovered,
    equipped: { ...START_LOADOUT },
    pullCount: 0,
    pity5Plus: 0,
  };
}

export function createArmory(options = {}) {
  return freshArmory(options);
}

function safeCounter(value, max = Number.MAX_SAFE_INTEGER - 1000) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.min(max, Math.floor(number));
}

function keyFromLegacy(value, slotHint = null) {
  if (typeof value === 'string') {
    if (CATALOG_BY_KEY[value]) return value;
    if (slotHint && CATALOG_BY_KEY[partKey(slotHint, value, 1)]) {
      return partKey(slotHint, value, 1);
    }
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  for (const candidate of [value.key, value.partKey, value.catalogKey]) {
    if (typeof candidate === 'string' && CATALOG_BY_KEY[candidate]) return candidate;
  }
  const slot = value.slot ?? slotHint;
  const id = value.id ?? value.kind ?? value.p?.id;
  const tier = value.tier ?? value.grade ?? value.t ?? 1;
  if (!slot || !id) return null;
  const key = partKey(slot, id, Number(tier));
  return CATALOG_BY_KEY[key] ? key : null;
}

function addInventoryCount(inventory, key, rawCount = 1) {
  if (!CATALOG_BY_KEY[key]) return;
  const count = safeCounter(rawCount);
  if (!count) return;
  inventory[key] = safeCounter((inventory[key] ?? 0) + count);
}

function ingestInventory(inventory, source) {
  if (Array.isArray(source)) {
    for (const item of source) {
      const key = keyFromLegacy(item, item?.slot ?? null);
      const count = item && typeof item === 'object'
        ? item.count ?? item.qty ?? item.amount ?? 1
        : 1;
      addInventoryCount(inventory, key, count);
    }
    return;
  }
  if (!source || typeof source !== 'object') return;
  for (const [mapKey, value] of Object.entries(source)) {
    let key = CATALOG_BY_KEY[mapKey] ? mapKey : null;
    if (!key && value && typeof value === 'object') {
      key = keyFromLegacy(value, value.slot ?? null);
    }
    const count = value && typeof value === 'object'
      ? value.count ?? value.qty ?? value.amount ?? 1
      : value;
    addInventoryCount(inventory, key, count);
  }
}

function ingestDiscovered(discovered, source) {
  if (Array.isArray(source)) {
    for (const value of source) {
      const key = keyFromLegacy(value, value?.slot ?? null);
      if (key) discovered[key] = true;
    }
    return;
  }
  if (!source || typeof source !== 'object') return;
  for (const [mapKey, value] of Object.entries(source)) {
    if (!value) continue;
    const key = CATALOG_BY_KEY[mapKey]
      ? mapKey
      : keyFromLegacy(value, value?.slot ?? null);
    if (key) discovered[key] = true;
  }
}

const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

function exactStarterInventory(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const starterKeys = Object.values(START_LOADOUT);
  return Object.keys(value).length === starterKeys.length
    && starterKeys.every((key) => own(value, key) && value[key] === 1);
}

function exactStarterEquipped(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value).length === SLOTS.length
    && SLOTS.every((slot) => own(value, slot) && value[slot] === START_LOADOUT[slot]);
}

function exactStarterDiscoveries(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const starterKeys = Object.values(START_LOADOUT);
  return Object.keys(value).length === starterKeys.length
    && starterKeys.every((key) => own(value, key) && value[key] === true);
}

// Only the two canonical pre-v3 start shapes receive the story reserve.  A
// missing version, a legacy alias, an extra/consumed copy, a changed loadout,
// or any pull/pity progress makes the save ineligible.  This deliberately
// avoids turning normalization into a general-purpose item grant.
function shouldGrantStarterReserve(nested) {
  if (nested.version !== 1 && nested.version !== 2) return false;
  if (nested.pullCount !== 0 || nested.pity5Plus !== 0) return false;
  if (!exactStarterInventory(nested.inventory)) return false;
  if (!exactStarterEquipped(nested.equipped)) return false;
  if (nested.version === 1) {
    return !own(nested, 'discovered')
      && !own(nested, 'collection')
      && !own(nested, 'seen');
  }
  return exactStarterDiscoveries(nested.discovered);
}

/**
 * Migrate a canonical armory, a whole save containing `armory`, or legacy
 * `{ inventory|owned|parts, equipped|loadout|gear }` shapes.
 * Unknown catalogue keys and serialized random traits are deliberately dropped.
 */
export function normalizeArmory(raw, options = {}) {
  const nested = raw && typeof raw === 'object' && raw.armory && typeof raw.armory === 'object'
    ? raw.armory
    : raw;
  // New campaigns enter through createArmory() and always receive the authored
  // reserve.  A missing/malformed armory in an arbitrary old save is not proof
  // that the story choice belongs there, so normalization falls back to the
  // four equipped starters only.
  if (!nested || typeof nested !== 'object') {
    return freshArmory(options, { includeStarterReserve: false });
  }

  const inventory = emptyInventory();
  const inventorySource = nested.inventory ?? nested.owned ?? nested.parts;
  ingestInventory(inventory, inventorySource);
  const discovered = emptyDiscovered();
  ingestDiscovered(discovered, nested.discovered ?? nested.collection ?? nested.seen);
  // Campaign progression is not represented inside the armory.  The caller
  // must therefore prove that this is an untouched first-day full save before
  // the strict v1/v2 armory-shape check is allowed to grant anything.
  const grantStarterReserve = options.allowStarterReserveGrant === true
    && shouldGrantStarterReserve(nested);

  const equippedSource = nested.equipped ?? nested.loadout ?? nested.gear ?? {};
  const equipped = {};
  for (const slot of SLOTS) {
    const legacy = keyFromLegacy(equippedSource?.[slot], slot);
    const key = legacy && CATALOG_BY_KEY[legacy]?.slot === slot
      ? legacy
      : START_LOADOUT[slot];
    equipped[slot] = key;
    if (!inventory[key]) inventory[key] = 1;
  }
  // Old saves had no permanent collection ledger. Every positive count and
  // equipped key is therefore a confirmed discovery and is backfilled once.
  for (const [key, count] of Object.entries(inventory)) {
    if (count > 0) discovered[key] = true;
  }
  for (const key of Object.values(equipped)) discovered[key] = true;
  if (grantStarterReserve) {
    inventory[STARTER_RESERVE.key] = STARTER_RESERVE.count;
    discovered[STARTER_RESERVE.key] = true;
  }

  const pullCount = safeCounter(
    nested.pullCount ?? nested.drawCount ?? nested.rollCount ?? nested.pulls,
  );
  const pity5Plus = safeCounter(
    nested.pity5Plus ?? nested.pityCount ?? nested.sinceHighTier ?? nested.pity,
    PITY_5_PLUS,
  );

  return {
    version: ARMORY_VERSION,
    gachaSeed: canonicalGachaSeed(nested.gachaSeed)
      ?? canonicalGachaSeed(options.gachaSeed)
      ?? createGachaSeed(options.seedSource),
    inventory,
    discovered,
    equipped,
    pullCount,
    pity5Plus,
  };
}

export function costForPull(count) {
  return PULL_COSTS[count] ?? null;
}

function seededDrawRandom(gachaSeed, pullIndex) {
  return mulberry32(hash32(`armory-pull\u001f${String(gachaSeed)}\u001f${pullIndex}`));
}

function chooseWeightedTier(random, minTier = 1, allowedTiers = null) {
  const allowed = allowedTiers ? new Set(allowedTiers) : null;
  const weighted = [];
  let total = 0;
  for (let tier = Math.max(1, minTier); tier <= 6; tier += 1) {
    if (allowed && !allowed.has(tier)) continue;
    const weight = TIER_PROBABILITIES[tier - 1];
    weighted.push({ tier, weight });
    total += weight;
  }
  if (!weighted.length || total <= 0) return null;
  let cursor = random() * total;
  for (const entry of weighted) {
    cursor -= entry.weight;
    if (cursor < 0) return entry.tier;
  }
  return weighted[weighted.length - 1].tier;
}

function undiscoveredAtStart(entry, discoveredAtStart) {
  return !discoveredAtStart[entry.key];
}

function candidatesFor(discoveredAtStart, minTier, newOnly) {
  return CATALOG.filter((entry) => (
    entry.tier >= minTier && (!newOnly || undiscoveredAtStart(entry, discoveredAtStart))
  ));
}

function hasCandidate(discoveredAtStart, minTier, newOnly) {
  return CATALOG.some((entry) => (
    entry.tier >= minTier && (!newOnly || undiscoveredAtStart(entry, discoveredAtStart))
  ));
}

function chooseCatalogueEntry(random, discoveredAtStart, { minTier = 1, newOnly = false } = {}) {
  const candidates = candidatesFor(discoveredAtStart, minTier, newOnly);
  if (!candidates.length) return null;
  const availableTiers = [...new Set(candidates.map((entry) => entry.tier))];
  const tier = chooseWeightedTier(random, minTier, availableTiers);
  const tierPool = candidates.filter((entry) => entry.tier === tier);
  return tierPool[Math.min(tierPool.length - 1, Math.floor(random() * tierPool.length))];
}

function failedPull(rawArmory, reason, count, cost, balance) {
  return {
    ok: false,
    reason,
    count,
    cost,
    spent: 0,
    balance,
    armory: rawArmory,
    results: [],
  };
}

/**
 * Atomic functional transaction.
 *
 * `pull(raw, { count: 1|10, balance })` never mutates `raw`.
 * Draws use the armory's separately persisted `gachaSeed` plus `pullCount`;
 * the campaign root seed is deliberately not read here.
 * On failure the exact input armory reference and balance are returned unchanged.
 */
export function pull(rawArmory, options = {}) {
  const count = Number(options.count ?? 1);
  const cost = costForPull(count);
  const rawBalance = options.balance ?? options.cheese;
  const balance = Number(rawBalance);

  if (!cost) return failedPull(rawArmory, 'invalid-count', count, null, rawBalance);
  if (!Number.isFinite(balance) || balance < 0) {
    return failedPull(rawArmory, 'invalid-balance', count, cost, rawBalance);
  }
  if (balance < cost) return failedPull(rawArmory, 'insufficient-funds', count, cost, balance);
  const base = normalizeArmory(rawArmory);
  const discoveredAtStart = { ...base.discovered };
  const discovered = { ...base.discovered };
  const inventory = { ...base.inventory };
  const hasUndiscoveredAtStart = CATALOG.some((entry) => (
    undiscoveredAtStart(entry, discoveredAtStart)
  ));
  let gotNew = false;
  let gotTier3 = false;
  let pity5Plus = base.pity5Plus;
  const results = [];

  for (let index = 0; index < count; index += 1) {
    const random = seededDrawRandom(base.gachaSeed, base.pullCount + index);
    const pityDue = pity5Plus >= PITY_5_PLUS;
    const guarantees = [];
    let minTier = pityDue ? 5 : 1;
    let newOnly = false;

    if (pityDue) guarantees.push('pity-5-plus');

    if (count === 10 && index === 8 && hasUndiscoveredAtStart && !gotNew) {
      // Reserve the ninth result for the new-item guarantee. If pity and new
      // cannot be combined, satisfy pity now and reserve the tenth instead.
      if (!pityDue || hasCandidate(discoveredAtStart, 5, true)) {
        newOnly = true;
        guarantees.push('ten-new');
      }
    }

    if (count === 10 && index === 9) {
      const needNew = hasUndiscoveredAtStart && !gotNew;
      const needTier3 = !gotTier3;
      if (needTier3) {
        minTier = Math.max(minTier, 3);
        guarantees.push('ten-tier-3');
      }
      if (needNew) {
        newOnly = true;
        guarantees.push('ten-new');
      }
    }

    let entry = chooseCatalogueEntry(random, discoveredAtStart, { minTier, newOnly });
    if (!entry && newOnly && pityDue) {
      // Defensive path for a migrated edge state. The ninth-result reservation
      // makes this unreachable in normal play, but pity must never be weakened.
      entry = chooseCatalogueEntry(random, discoveredAtStart, { minTier, newOnly: false });
      const newIndex = guarantees.indexOf('ten-new');
      if (newIndex >= 0) guarantees.splice(newIndex, 1);
    }
    if (!entry) {
      return failedPull(rawArmory, 'catalogue-exhausted', count, cost, balance);
    }

    const isNew = !discovered[entry.key];
    const wasUnowned = undiscoveredAtStart(entry, discoveredAtStart);
    inventory[entry.key] = safeCounter((inventory[entry.key] ?? 0) + 1);
    discovered[entry.key] = true;
    gotNew ||= wasUnowned;
    gotTier3 ||= entry.tier >= 3;
    pity5Plus = entry.tier >= 5 ? 0 : Math.min(PITY_5_PLUS, pity5Plus + 1);

    results.push({
      ...entry,
      traits: entry.traits.slice(),
      isNew,
      wasUnownedAtBatchStart: wasUnowned,
      guarantees,
    });
  }

  if (count === 10 && (!gotTier3 || (hasUndiscoveredAtStart && !gotNew))) {
    return failedPull(rawArmory, 'guarantee-invariant', count, cost, balance);
  }

  const armory = {
    version: ARMORY_VERSION,
    gachaSeed: base.gachaSeed,
    inventory,
    discovered,
    equipped: { ...base.equipped },
    pullCount: base.pullCount + count,
    pity5Plus,
  };

  return {
    ok: true,
    reason: null,
    count,
    cost,
    spent: cost,
    balance: balance - cost,
    armory,
    results,
  };
}

/** Equip an owned catalogue key in its matching slot without mutating input. */
export function equip(rawArmory, slot, key) {
  if (!SLOTS.includes(slot)) {
    return { ok: false, reason: 'invalid-slot', armory: rawArmory };
  }
  const entry = CATALOG_BY_KEY[key];
  if (!entry || entry.slot !== slot) {
    return { ok: false, reason: 'slot-mismatch', armory: rawArmory };
  }
  const base = normalizeArmory(rawArmory);
  if (!(base.inventory[key] > 0)) {
    return { ok: false, reason: 'not-owned', armory: rawArmory };
  }
  const armory = {
    ...base,
    inventory: { ...base.inventory },
    equipped: { ...base.equipped, [slot]: key },
  };
  return { ok: true, reason: null, armory, equipped: { ...entry, traits: entry.traits.slice() } };
}

function invalidUpgradePreview(reason, key) {
  return {
    ok: false,
    reason,
    canUpgrade: false,
    key,
    source: null,
    result: null,
    requiredCopies: UPGRADE_COPY_COST,
    sourceCount: 0,
    consumes: UPGRADE_COPY_COST,
    cheeseCost: 0,
    willAutoEquip: false,
  };
}

function upgradePreviewFromArmory(armory, sourceKey) {
  const entry = CATALOG_BY_KEY[sourceKey];
  if (!entry) return invalidUpgradePreview('invalid-key', sourceKey);

  const sourceCount = armory.inventory[sourceKey] ?? 0;
  const source = catalogueView(entry, armory);
  if (entry.tier >= 6) {
    return {
      ...invalidUpgradePreview('max-tier', sourceKey),
      ok: true,
      source,
      sourceCount,
    };
  }

  const resultEntry = CATALOG_BY_KEY[partKey(entry.slot, entry.id, entry.tier + 1)];
  if (!resultEntry) return invalidUpgradePreview('upgrade-target-missing', sourceKey);
  const canUpgrade = sourceCount >= UPGRADE_COPY_COST;
  const willAutoEquip = armory.equipped[entry.slot] === sourceKey;
  return {
    ok: true,
    reason: canUpgrade ? null : 'insufficient-copies',
    canUpgrade,
    key: sourceKey,
    source,
    result: catalogueView(resultEntry, armory),
    requiredCopies: UPGRADE_COPY_COST,
    sourceCount,
    sourceCountAfter: canUpgrade ? sourceCount - UPGRADE_COPY_COST : sourceCount,
    resultCountAfter: (armory.inventory[resultEntry.key] ?? 0) + (canUpgrade ? 1 : 0),
    consumes: UPGRADE_COPY_COST,
    cheeseCost: 0,
    willAutoEquip,
  };
}

/** Stable UI preview for one exact slot + family + grade catalogue key. */
export function upgradePreview(rawArmory, sourceKey) {
  if (!CATALOG_BY_KEY[sourceKey]) return invalidUpgradePreview('invalid-key', sourceKey);
  return upgradePreviewFromArmory(normalizeArmory(rawArmory), sourceKey);
}

/** All currently actionable 3-to-1 upgrades, in stable catalogue order. */
export function eligibleUpgrades(rawArmory) {
  const armory = normalizeArmory(rawArmory);
  return CATALOG
    .filter((entry) => entry.tier < 6 && (armory.inventory[entry.key] ?? 0) >= UPGRADE_COPY_COST)
    .map((entry) => upgradePreviewFromArmory(armory, entry.key));
}

/**
 * Atomically consume three identical copies and add one canonical next-tier
 * copy. No cheese is read or charged. A source that was equipped is replaced
 * by the upgraded result only after every validation succeeds.
 */
export function upgradePart(rawArmory, sourceKey) {
  if (!CATALOG_BY_KEY[sourceKey]) {
    return { ok: false, reason: 'invalid-key', armory: rawArmory };
  }
  const armory = normalizeArmory(rawArmory);
  const preview = upgradePreviewFromArmory(armory, sourceKey);
  if (!preview.canUpgrade) {
    return { ok: false, reason: preview.reason, armory: rawArmory, preview };
  }

  const inventory = { ...armory.inventory };
  const sourceCountAfter = inventory[sourceKey] - UPGRADE_COPY_COST;
  if (sourceCountAfter > 0) inventory[sourceKey] = sourceCountAfter;
  else delete inventory[sourceKey];
  inventory[preview.result.key] = safeCounter((inventory[preview.result.key] ?? 0) + 1);
  const discovered = { ...armory.discovered, [sourceKey]: true, [preview.result.key]: true };

  const equipped = { ...armory.equipped };
  if (preview.willAutoEquip) equipped[preview.source.slot] = preview.result.key;
  const nextArmory = { ...armory, inventory, discovered, equipped };

  return {
    ok: true,
    reason: null,
    armory: nextArmory,
    preview,
    source: preview.source,
    result: {
      ...preview.result,
      traits: preview.result.traits.slice(),
      count: inventory[preview.result.key],
      owned: true,
      equipped: equipped[preview.result.slot] === preview.result.key,
    },
    consumed: UPGRADE_COPY_COST,
    cheeseCost: 0,
    autoEquipped: preview.willAutoEquip,
  };
}

function resolvePart(partSource, slot, id) {
  const lookup = partSource?.PART_BY_ID ?? partSource;
  const key = `${slot}:${id}`;
  if (typeof lookup === 'function') return lookup(slot, id) ?? null;
  if (lookup instanceof Map) return lookup.get(key) ?? null;
  if (lookup && typeof lookup === 'object') return lookup[key] ?? null;
  return null;
}

/**
 * Convert saved compact keys into the wrapper shape consumed by resolveGear:
 * `{ stone:{p,t,traits}, cradle:{...}, cord:{...}, loop:{...} }`.
 * Pass `PART_BY_ID`, the full gear module, a Map, or `(slot,id) => part`.
 */
export function hydrateGear(rawArmory, partSource) {
  const armory = normalizeArmory(rawArmory);
  const gear = {};
  for (const slot of SLOTS) {
    const entry = CATALOG_BY_KEY[armory.equipped[slot]];
    const part = resolvePart(partSource, slot, entry.id);
    if (!part) {
      throw new TypeError(`hydrateGear: missing combat definition for ${slot}:${entry.id}`);
    }
    gear[slot] = { p: part, t: entry.tier, traits: entry.traits.slice() };
  }
  return gear;
}

function catalogueView(entry, armory) {
  const count = armory.inventory[entry.key] ?? 0;
  return {
    ...entry,
    traits: entry.traits.slice(),
    count,
    owned: count > 0,
    discovered: Boolean(armory.discovered[entry.key]),
    equipped: armory.equipped[entry.slot] === entry.key,
  };
}

/** Stable UI model: all 144 entries, ownership counts and four equipped cards. */
export function armoryView(rawArmory) {
  const armory = normalizeArmory(rawArmory);
  const inventory = CATALOG.map((entry) => catalogueView(entry, armory));
  const equipped = Object.fromEntries(SLOTS.map((slot) => {
    const entry = CATALOG_BY_KEY[armory.equipped[slot]];
    return [slot, catalogueView(entry, armory)];
  }));
  const upgradeCandidates = CATALOG
    .filter((entry) => entry.tier < 6 && (armory.inventory[entry.key] ?? 0) >= UPGRADE_COPY_COST)
    .map((entry) => upgradePreviewFromArmory(armory, entry.key));
  return {
    version: armory.version,
    catalogCount: CATALOG.length,
    ownedCount: inventory.filter((entry) => entry.discovered).length,
    availableCount: inventory.filter((entry) => entry.owned).length,
    totalCopies: inventory.reduce((sum, entry) => sum + entry.count, 0),
    pullCount: armory.pullCount,
    pity5Plus: armory.pity5Plus,
    pityRemaining: Math.max(0, PITY_5_PLUS - armory.pity5Plus),
    upgradeCandidateCount: upgradeCandidates.length,
    upgradeCandidates,
    inventory,
    equipped,
  };
}
